import { useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Box, Stack, Typography } from '@mui/material';
import EnvironmentGuard from '../../../../components/EnvironmentGuard';
import ResultsTable from './ResultsTable';
import Win32AppsOverview from './Win32AppsOverview';
import { EXPANDED_ROWS_LIMIT } from 'src/constants/table';
import { useInfiniteWdacResults } from 'src/hooks/wdac';
import { APPLICATION_TRUST_STATUS } from 'src/utils/enums/applicationTrustStatus';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';

// This endpoint takes a nested ordering model rather than the newer flat sortColumn/sortDesc.
const buildSortQueryParams = sort =>
  sort.column
    ? { 'OrderWdacQuery.sortBy': sort.column, 'OrderWdacQuery.order': sort.direction }
    : {};

const DEFAULT_SORT = { column: 'devices_last30days', direction: 'desc' };

const DEFAULT_LIST_FILTERS = {
  applicationTrustStatuses: [
    APPLICATION_TRUST_STATUS.Unknown,
    APPLICATION_TRUST_STATUS.Trusted,
    APPLICATION_TRUST_STATUS.InProgress,
  ],
  tagId: '',
  warningRowsOnly: true,
};

const Win32Apps = () => {
  const pageLimit = EXPANDED_ROWS_LIMIT;
  const [searchValue, setSearchValue] = useState('');
  const [listFilters, setListFilters] = useState(
    () => readFilterParams(DEFAULT_LIST_FILTERS) ?? DEFAULT_LIST_FILTERS,
  );
  const [sort, setSort] = useState(DEFAULT_SORT);

  useFilterParams(listFilters);

  const apiQuery = useMemo(() => {
    const params = {
      ...buildSortQueryParams(sort),
    };

    const trimmedSearch = typeof searchValue === 'string' ? searchValue.trim() : '';
    if (trimmedSearch) {
      params.search = trimmedSearch;
    }

    const statuses = (listFilters?.applicationTrustStatuses || [])
      .map(value => String(value))
      .filter(Boolean);
    if (statuses.length > 0) {
      params.ApplicationTrustStatuses = statuses;
    }

    if (
      listFilters?.tagId !== undefined &&
      listFilters?.tagId !== null &&
      listFilters?.tagId !== ''
    ) {
      params.CustomerEnvTagId = listFilters.tagId;
    }

    if (listFilters?.warningRowsOnly) {
      params.WarningRowsOnly = true;
    }

    return params;
  }, [searchValue, sort, listFilters]);

  const wdacResultsQuery = useInfiniteWdacResults({
    apiQuery,
    limit: pageLimit,
    enabled: true,
  });

  const results = useMemo(
    () => wdacResultsQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [wdacResultsQuery.data],
  );

  const totalCount = useMemo(() => {
    if (!wdacResultsQuery.data?.pages?.length) {
      return 0;
    }

    return wdacResultsQuery.data.pages[0]?.totalCount || 0;
  }, [wdacResultsQuery.data]);

  const loadMore = useCallback(() => {
    if (wdacResultsQuery.hasNextPage) {
      wdacResultsQuery.fetchNextPage();
    }
  }, [wdacResultsQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: wdacResultsQuery.isFetchingNextPage,
    hasNextPage: wdacResultsQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  return (
    <EnvironmentGuard>
      <Stack gap={2.5}>
        <Win32AppsOverview onFiltersChange={setListFilters} />

        <Box>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: '1.05rem',
              lineHeight: 1.4,
              mb: 1,
            }}>
            Win32 Apps
          </Typography>
          <ResultsTable
            isLoading={wdacResultsQuery.isLoading}
            results={results}
            sentryRef={sentryRef}
            isFetchingNextPage={wdacResultsQuery.isFetchingNextPage}
            totalCount={totalCount}
            query={apiQuery}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            listFilters={listFilters}
            onListFiltersChange={setListFilters}
            sort={sort}
            onSortChange={setSort}
          />
        </Box>
      </Stack>
    </EnvironmentGuard>
  );
};

export default Win32Apps;
